import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { rupee, waLink, cartWaText } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Minus, Plus, Trash2, MessageCircle, ShoppingBag, ArrowRight } from "lucide-react";

export default function Cart() {
  const { cart, updateQty, removeFromCart, subtotal, settings } = useApp();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [coupon, setCoupon] = useState(null);

  const discount = coupon?.discount || 0;
  const delivery = subtotal >= 500 ? 0 : subtotal > 0 ? 40 : 0;
  const tax = Math.round(Math.max(subtotal - discount, 0) * 0.05);
  const total = Math.round(Math.max(subtotal - discount, 0) + tax + delivery);

  const applyCoupon = () => {
    if (!code.trim()) return;
    api.post("/coupons/validate", { code: code.trim(), subtotal }).then((r) => { setCoupon(r.data); toast.success(`Coupon applied: -${rupee(r.data.discount)}`); })
      .catch((e) => toast.error(e.response?.data?.detail || "Invalid coupon"));
  };

  if (cart.length === 0) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center">
      <ShoppingBag className="w-16 h-16 text-sage mx-auto mb-4" />
      <h1 className="font-heading text-4xl text-forest">Your cart is empty</h1>
      <p className="text-brown/70 mt-2">Discover organic wellness essentials made for you.</p>
      <Link to="/shop"><Button className="mt-6 rounded-full h-12 px-8 bg-forest hover:bg-earth text-cream">Start Shopping</Button></Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
      <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight mb-8">Shopping Cart</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {cart.map((i) => (
            <div key={i.product_id} className="bg-white rounded-2xl soft-shadow p-4 flex gap-4" data-testid={`cart-item-${i.product_id}`}>
              <Link to={`/product/${i.slug}`} className="w-24 h-24 rounded-xl overflow-hidden bg-sand shrink-0"><img src={i.image} alt={i.name} className="w-full h-full object-cover" /></Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${i.slug}`} className="font-500 text-ink hover:text-terracotta line-clamp-2">{i.name}</Link>
                <div className="text-xs text-muted-foreground">{i.pack_size} • {i.sku}</div>
                <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                  <div className="flex items-center border border-sage/50 rounded-full">
                    <button onClick={() => updateQty(i.product_id, i.qty - 1)} className="w-8 h-9 flex items-center justify-center text-forest" data-testid={`cart-minus-${i.product_id}`}><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-8 text-center text-sm">{i.qty}</span>
                    <button onClick={() => updateQty(i.product_id, i.qty + 1)} className="w-8 h-9 flex items-center justify-center text-forest" data-testid={`cart-plus-${i.product_id}`}><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <span className="font-600 text-forest">{rupee(i.price * i.qty)}</span>
                </div>
              </div>
              <button onClick={() => removeFromCart(i.product_id)} data-testid={`cart-remove-${i.product_id}`} className="text-muted-foreground hover:text-terracotta self-start"><Trash2 className="w-5 h-5" /></button>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl soft-shadow p-6 sticky top-28">
            <h3 className="font-heading text-2xl text-forest mb-4">Order Summary</h3>
            <div className="flex gap-2 mb-4">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Discount code" className="rounded-full bg-sand border-sage/40" data-testid="coupon-input" />
              <Button onClick={applyCoupon} variant="outline" className="rounded-full border-forest text-forest shrink-0" data-testid="apply-coupon-btn">Apply</Button>
            </div>
            <div className="space-y-2 text-sm border-t border-sage/20 pt-4">
              <Row label="Subtotal" value={rupee(subtotal)} />
              {discount > 0 && <Row label={`Discount (${coupon.code})`} value={`- ${rupee(discount)}`} accent />}
              <Row label="Tax (5%)" value={rupee(tax)} />
              <Row label="Delivery" value={delivery === 0 ? "FREE" : rupee(delivery)} />
              <div className="flex justify-between border-t border-sage/20 pt-3 mt-2"><span className="font-600 text-lg text-forest">Total</span><span className="font-600 text-lg text-forest" data-testid="cart-total">{rupee(total)}</span></div>
            </div>
            <Button onClick={() => nav("/checkout")} className="w-full mt-5 rounded-full h-12 bg-forest hover:bg-earth text-cream" data-testid="checkout-btn">Proceed to Checkout <ArrowRight className="w-4 h-4 ml-1" /></Button>
            <a href={waLink(settings.whatsapp_number, cartWaText(cart, { subtotal, discount, total }))} target="_blank" rel="noreferrer" data-testid="cart-wa-btn"
              className="w-full mt-3 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1eb85a] text-white rounded-full h-12 font-500 transition-colors">
              <MessageCircle className="w-5 h-5" /> Ask on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return <div className="flex justify-between"><span className="text-brown/70">{label}</span><span className={accent ? "text-terracotta font-500" : "text-ink"}>{value}</span></div>;
}
